import { ResizeSensor } from "css-element-queries"
import { fabric } from "fabric"
import Cookies from "js-cookie"
import { debounce, throttle, cloneDeep } from "lodash-es"
import { type Store } from "redux"
import { SimpleEventDispatcher, SignalDispatcher } from "strongly-typed-events"
import invariant from "tiny-invariant"
import { isImage, isImageObj, isText, isTextObj } from "../util/types"
import Contour from "./contour"
import { loadControlImages } from "./objects/controls"
import { type DrawableMixin } from "./objects/drawable_mixin"
import type SignImage from "./objects/sign_image"
import type SignText from "./objects/sign_text"
import SignDimensions from "./sign_dimensions"
import SignHistory from "./sign_history"
import SigncodeConverter from "./signcode_converter"
import StateBridge from "./state_bridge"
import ClientData from "@/common/client_data"
import env from "@/common/env"
import i18n from "@/common/locales"
import storageLocal from "@/common/storage"
import { stringifyObj, clamp, isTablet, metaNameSign } from "@/common/util"
import Calc from "@/common/util/calculator"
import misc, { isMobile } from "@/common/util/misc"
import { getRotateCursor } from "@/signomatic/app/objects/controls"
import { calcMinimaticExtraPadding } from "@/signomatic/app/objects/sign_text"
import SignDrawables, { type DrawableObject } from "@/signomatic/app/sign_drawables"
import SignExport from "@/signomatic/app/sign_export"
import SignObjects, { type SignObjecType, type SignObject } from "@/signomatic/app/sign_objects"
import {
  correctState,
  defaultState,
  applyPreset,
  allPresets,
  dataWithPreset,
  dataWithSignTypeOverride,
  reduceOption,
  currentMinText,
  matchPreset,
} from "@/signomatic/app/sign_validator"
import { ie, iosVersion, macOs, ios } from "@/signomatic/util"
import { type ColorSet } from "@/signomatic/util/colors"
import Fonts, { type Fontdata } from "@/signomatic/util/fonts"
import logger, { info, error } from "@/signomatic/util/logger"

declare global {
  interface Window {
    sgn: Sign
    sign: Sign
  }
}

export interface ActiveObjectInfo {
  width: number
  height: number
  left: number
  right: number
  top: number
  bottom: number
  angle: number
}

export interface SignStateData {
  state: SignState
  data: SignData[]
}

export interface SignViewportState {
  zoom: number
  pan: boolean
}
export interface MetaSignTypeChange {
  metaProduct: string
  signType?: string
}
export interface SignStateUpdate {
  update: Partial<SignState>
  state: SignState
}

interface PreviousSignData {
  state: SignState
  metaProduct: string | null
  signType: string
}

type ClearHandler = () => Promise<void>
type StateTest = [(state: SignState, sign: Sign) => boolean, string]

export const minZoom = 0.25
export const maxZoom = 2

class Sign {
  static isImage(state: SignObjectState): state is SignImageState {
    return isImage(state)
  }

  static isText(state: SignObjectState): state is SignTextState {
    return isText(state)
  }

  static isImageObj(obj: SignObject): obj is SignImage {
    return isImageObj(obj)
  }

  static isTextObj(obj: SignObject): obj is SignText {
    return isTextObj(obj)
  }

  /**
   * Compare object state identity Symbols to determine if it's the same object
   */
  static sameObject(a: SignObjectState, b: SignObjectState): boolean {
    if (!a || !b) {
      return false
    }

    return a.identity === b.identity
  }

  signType: string | null = null
  metaProduct: string | undefined = undefined
  canvas: fabric.Canvas
  visualClipping = true
  fontdata: Fontdata
  readonly minimatic: boolean
  private readonly signCLI: boolean
  private readonly contour: Contour
  private readonly export: SignExport
  private readonly drawables: SignDrawables
  private readonly history: SignHistory
  private readonly dimensions: SignDimensions
  private readonly signTypes: string[]
  private readonly onSignTypeChangedEvent = new SimpleEventDispatcher<string>()
  private readonly onMetaSignTypeChangedEvent = new SimpleEventDispatcher<MetaSignTypeChange>()
  private readonly onSignChangedEvent = new SimpleEventDispatcher<SignState>()
  private readonly onStateUpdatedEvent = new SimpleEventDispatcher<SignStateUpdate>()
  private readonly onSignDataChangedEvent = new SimpleEventDispatcher<SignStateData>()
  private readonly onViewportStateChangedEvent = new SimpleEventDispatcher<SignViewportState>()
  private readonly onDoubleClickEvent = new SignalDispatcher()
  private readonly onEmptyClickEvent = new SignalDispatcher()
  private readonly onInlineEditEvent = new SignalDispatcher()
  private readonly onLoadEvent = new SignalDispatcher()
  private readonly onOpentypeToggleEvent = new SignalDispatcher()
  private readonly guidelines: fabric.Object[] = []
  private stateTesters: StateTest[] = []
  private onClearHandlers: ClearHandler[] = []
  private signData: SignData[]
  private objects: SignObjects
  private canvasWrapper: HTMLElement
  private signState: SignState
  private previousContourState: string
  private exampleID: number | null = null
  private signCategoryID: number | null = null
  private examplerID: number | null = null
  private recommended: string
  private drawing: boolean
  private flipCleanup: number
  private clearing: boolean
  private loaded: boolean
  private useOpentype = true
  private panning = false
  private panningToggled = false
  private panningKeyDown = false
  private lastPointerX = 0
  private lastPointerY = 0
  private disableDraw = false
  private lastObjectAngle = 0

  static get opentypeText() {
    return window.sign.opentypeText
  }

  constructor(minimatic?: boolean, signCLI?: boolean, fontdata?: Fontdata, skipGlobal = false) {
    logger.init()

    // for text scaleX scale transform with small sizes
    fabric.Object.NUM_FRACTION_DIGITS = 3

    // make a copy of the sign data because we modify it to allow legacy sign sizes
    this.signData = cloneDeep(this.originalDatas)
    this.signTypes = this.signData.map(data => data.name)
    this.history = new SignHistory(this)
    this.dimensions = new SignDimensions(this)
    this.objects = new SignObjects(this, this.history)
    this.drawables = new SignDrawables(this)
    this.export = new SignExport(this, this.objects)
    this.contour = new Contour(this, this.objects)
    this.minimatic = minimatic || false
    this.signCLI = signCLI || false
    if (fontdata) {
      this.fontdata = fontdata
    }

    if (!skipGlobal) {
      window.sgn = window.sign = this
    }
  }

  get data(): SignData {
    const signData = this.signData.find(data => data.name === this.signType)

    if (!signData) {
      throw new Error(`Sign data missing for ${this.signType}`)
    }

    return signData
  }

  get originalDatas(): SignData[] {
    return ClientData.signData || []
  }

  get originalData(): SignData {
    const signData = this.originalDatas.find(data => data.name === this.signType)

    if (!signData) {
      throw new Error(`Original sign data missing for ${this.signType}`)
    }

    return signData
  }

  get fullData(): SignData {
    const overrideData = dataWithSignTypeOverride(this.data, this.signType, this.state, this.fontdata)
    return dataWithPreset(overrideData, this.state)
  }

  get validatedData(): SignData[] {
    return this.signData.map(data => {
      const overrideData = dataWithSignTypeOverride(data, null, this.state, this.fontdata)
      data = dataWithPreset(overrideData, this.state)
      const borders = reduceOption(data.borders, this.state, "border")
      const fixmethods = reduceOption(data.fixmethods, this.state, "fixmethod")
      const materials = reduceOption(data.materials, this.state, "material")
      const shapes = reduceOption(data.shapes, this.state, "shape")
      const patterns = reduceOption(data.patterns, this.state, "pattern")

      return { ...data, borders, fixmethods, materials, shapes, patterns }
    })
  }

  get state() {
    if (this.signState && (!this.signState.widthPx || !this.signState.heightPx)) {
      this.calcState(this.signState)
    }

    return this.signState
  }

  set state(newState) {
    this.calcState(newState)
    this.signState = newState
    this.drawables.setState(newState)
  }

  get scale() {
    return this.canvas ? this.canvas.getZoom() : 1
  }

  get defaultScale() {
    return this.calcScale()
  }

  get production() {
    return this.fullData.production
  }

  get pixelRatio() {
    return fabric.devicePixelRatio
  }

  get centerX() {
    return this.canvas.getWidth() / 2
  }

  get centerY() {
    return this.canvas.getHeight() / 2
  }

  get customSizeAllowed() {
    const hasPresets = this.signType && this.data && allPresets(this.data).length > 0

    return (
      !hasPresets ||
      this.signType === "double_sided_sign" ||
      this.signType === "wood_sign" ||
      (this.signType === "label_plates" &&
        this.state &&
        ["regular", "aluminium", "aluminium_lacquered", "stainless"].includes(this.state.material))
    )
  }

  get isLoaded() {
    return this.loaded
  }

  get activeObject() {
    return this.canvas.getActiveObject()
  }

  get clipObjects() {
    return this.state && this.state.shape !== "contour"
  }

  get minTextSize() {
    return currentMinText(this.originalData, this.state)
  }

  get opentypeText() {
    return this.useOpentype
  }

  set opentypeText(on: boolean) {
    if (this.useOpentype === on) {
      return
    }

    info(`Setting opentype to ${on}`)
    this.useOpentype = on
    this.onOpentypeToggleEvent.dispatch()
    this.draw()
  }

  updateFontdata(fontdata: Fontdata) {
    this.fontdata = fontdata
  }

  get canvasHasDefaultCursor() {
    if (this.canvas && this.canvas.getSelectionElement().style) {
      return this.canvas.getSelectionElement().style.cursor === this.canvas.defaultCursor
    }

    return null
  }

  set canvasCursor(cursor: string) {
    if (this.canvas) {
      this.canvas.setCursor(cursor)
    }
  }

  get allObjects() {
    return this.objects.all()
  }

  get clipShapeBorder() {
    return ["direction_left", "direction_right", "triangle"].includes(this.state?.shape) && this.drawables?.hasBorder
  }

  get panningActive() {
    return this.panning
  }

  get objectsDisabled() {
    return !this.data.images && !this.data.texts
  }

  get nodeCanvas() {
    if (!env.isJSDOM) {
      throw new Error("Calling nodeCanvas while not on node")
    }

    return fabric.util.getNodeCanvas(this.canvas.getElement())
  }

  get unsavedChanges() {
    return this.history.unsavedChanges
  }

  get blankSign() {
    if (["contour_cut", "vinyl_text"].includes(this.signType || "")) {
      return !this.state.objects.find(obj => Sign.isImage(obj) || (Sign.isText(obj) && !misc.isBlank(obj.text)))
    }

    if (this.signType === "sticker" && this.state.contour) {
      return !this.state.objects.find(obj => Sign.isImage(obj) || (Sign.isText(obj) && !misc.isBlank(obj.text)))
    }

    return false
  }

  get dimensionsWidth() {
    const dimensionsGroup = this.canvas.getObjects().find(obj => obj.name === "width_dimensions") as fabric.Group

    if (dimensionsGroup) {
      const textObj = dimensionsGroup._objects.find((obj: fabric.Object) => obj.name === "width") as fabric.IText

      return textObj.text
    }

    return ""
  }

  get dimensionsHeight() {
    const dimensionsGroup = this.canvas.getObjects().find(obj => obj.name === "height_dimensions") as fabric.Group

    if (dimensionsGroup) {
      const textObj = dimensionsGroup._objects.find((obj: fabric.Object) => obj.name === "height") as fabric.IText

      return textObj.text
    }

    return ""
  }

  get dimensionsThickness() {
    const dimensionsGroup = this.canvas.getObjects().find(obj => obj.name === "width_dimensions") as fabric.Group

    if (dimensionsGroup) {
      const textObj = dimensionsGroup._objects.find((obj: fabric.Object) => obj.name === "thickness") as fabric.IText

      return textObj.text
    }

    return ""
  }

  get hasColorOptions() {
    if (!this.state) {
      return false
    }

    if (["brass_sign", "wood_sign"].includes(this.signType || "")) {
      return false
    }

    if (this.signType === "color_badge") {
      const presets = allPresets(this.data)
      const preset = presets.find(preset => matchPreset(this.state, preset))
      return !preset?.settings.color
    }

    return true
  }

  get canRestorePreviousSign() {
    if (storageLocal.getItem("sign_modified")) {
      return false
    }

    if (!ClientData.editing && !ClientData.loadSignType && !ClientData.exampleID) {
      return false
    }

    return storageLocal.getEncryptedItem("restorable_sign") !== null
  }

  get canShowResetTooltip() {
    const signomaticVisited = storageLocal.getItem("signomatic_visited") === "true"
    const resetTooltipClosed = Cookies.get("reset_tooltip_closed") === "true"

    if (
      storageLocal.getItem("signType") &&
      storageLocal.getItem("sign_modified") &&
      signomaticVisited &&
      !ClientData.editing &&
      !ClientData.exampleID &&
      !ClientData.loadSignType &&
      !resetTooltipClosed
    ) {
      return true
    }

    return false
  }

  /**
   * Events
   */

  get onSignTypeChanged() {
    return this.onSignTypeChangedEvent.asEvent()
  }

  get onMetaSignTypeChanged() {
    return this.onMetaSignTypeChangedEvent.asEvent()
  }

  get onSignChanged() {
    return this.onSignChangedEvent.asEvent()
  }

  get onStateUpdated() {
    return this.onStateUpdatedEvent.asEvent()
  }

  get onSignDataChanged() {
    return this.onSignDataChangedEvent.asEvent()
  }

  get onViewportStateChanged() {
    return this.onViewportStateChangedEvent.asEvent()
  }

  get onDoubleClick() {
    return this.onDoubleClickEvent.asEvent()
  }

  get onEmptyClick() {
    return this.onEmptyClickEvent.asEvent()
  }

  get onInlineEdit() {
    return this.onInlineEditEvent.asEvent()
  }

  get onLoad() {
    return this.onLoadEvent.asEvent()
  }

  get onOpentypeToggle() {
    return this.onOpentypeToggleEvent.asEvent()
  }

  get onActiveChanged() {
    return this.objects.onActiveChangedEvent.asEvent()
  }

  get onActiveInfoChanged() {
    return this.objects.onActiveInfoChangedEvent.asEvent()
  }

  get onEmojiUsed() {
    return this.objects.onEmojiUsedEvent.asEvent()
  }

  get onGlyphsPresent() {
    return this.objects.onGlyphsPresentEvent.asEvent()
  }

  get onRotatingStarted() {
    return this.objects.onRotatingStartedEvent.asEvent()
  }

  get onRotatingStopped() {
    return this.objects.onRotatingStoppedEvent.asEvent()
  }

  get onHistoryChanged() {
    return this.history.onHistoryChangedEvent.asEvent()
  }

  get onObjectPositionChanged() {
    return this.objects.onObjectPositionChangedEvent.asEvent()
  }

  /**
   * Initializes fabric, restores any previous state, and performs first draw
   */
  async init(canvasEl: HTMLCanvasElement, loadState?: any, nodeWidth?: number, nodeHeight?: number) {
    if (!canvasEl.parentElement) {
      throw new Error("Canvas parent not found")
    }

    fabric.disableStyleCopyPaste = true
    this.canvasWrapper = canvasEl.parentElement
    const width = this.canvasWrapper.clientWidth
    const height = this.canvasWrapper.clientHeight

    if (fabric.devicePixelRatio > 1 && (iosVersion(12) || macOs())) {
      fabric.maxCacheSideLimit = 1024 // original 4096
      fabric.perfLimitSizeTotal = 524288 // original 2097152
    }

    this.canvas = new fabric.Canvas(canvasEl, {
      centeredScaling: true,
      preserveObjectStacking: true,
      allowTouchScrolling: false,
      controlsAboveOverlay: true,
      enablePointerEvents: false,
    })

    this.canvas.selection = false

    if (env.isJSDOM) {
      this.canvas.enableRetinaScaling = false
      this.canvas.setWidth(nodeWidth || 1000)
      this.canvas.setHeight(nodeHeight || 560)
    } else {
      this.canvas.enableRetinaScaling = true
      this.canvas.setWidth(width)
      this.canvas.setHeight(height)
    }

    if (typeof window !== "undefined") {
      try {
        const Hammer = (await import("hammerjs")).default
        const hammertime = new Hammer(this.canvas.upperCanvasEl)
        hammertime.get("pinch").set({ enable: true })
        hammertime.get("pan").set({ direction: Hammer.DIRECTION_ALL })

        hammertime.on("panstart", e => {
          if (this.panningKeyDown || this.panningToggled) {
            this.startPan()
            this.lastPointerX = e.center.x
            this.lastPointerY = e.center.y
          }
        })

        hammertime.on("pan", e => {
          if (this.panning) {
            const vpt = this.canvas.viewportTransform || []
            vpt[4] += e.center.x - this.lastPointerX
            vpt[5] += e.center.y - this.lastPointerY
            this.setViewportTransform(vpt)

            this.lastPointerX = e.center.x
            this.lastPointerY = e.center.y
          }
        })

        hammertime.on("panend", () => {
          if (this.panning) {
            this.stopPan()
          }
        })

        let initialZoom = 1
        let defaultScale = 1
        let minPinch = 1
        let maxPinch = 1
        let pinchTop = 1

        hammertime.on("pinchstart", e => {
          if (this.objects.active) {
            e.srcEvent.stopPropagation()
            return
          }

          this.stopPan()

          initialZoom = this.canvas.getZoom()
          defaultScale = this.calcScale()
          minPinch = defaultScale * minZoom
          maxPinch = defaultScale * maxZoom
          pinchTop = this.canvas.upperCanvasEl.getBoundingClientRect().top
        })

        hammertime.on("pinchin pinchout", e => {
          const zoom = 1
          if (isMobile() && this.onViewport() && zoom >= this.getZoom() && e.type === "pinchin") {
            e.preventDefault()
            return
          }

          this.canvas.zoomToPoint(
            new fabric.Point(e.center.x, e.center.y - pinchTop),
            clamp(initialZoom * e.scale, minPinch, maxPinch)
          )
          this.dispatchViewportState()
          this.redrawAfterZoom()
        })
      } catch (e) {
        error("Failed to load HammerJS")
        error(e)
      }
    }

    const objDelKey = macOs() || ios() ? "Backspace" : "Delete"

    loadControlImages()

    document.addEventListener("keydown", e => {
      if (e.target instanceof Element && e.target.nodeName == "TEXTAREA") {
        const activeObject = this.canvas.getActiveObject()

        if (this.objects.isSignObject(activeObject) && this.objects.get(activeObject.state)) {
          const target = activeObject as SignText

          if (target.isEditing && target.isPlaceholder) {
            target.opacity = 1
            target.isPlaceholder = false
          }
        }

        return true
      }

      if (e.key === objDelKey) {
        if (e.target instanceof Element && (e.target.nodeName == "INPUT" || e.target.nodeName == "TEXTAREA")) {
          return
        }

        e.preventDefault()
        const activeObject = this.canvas.getActiveObject()
        if (activeObject) {
          this.objects.remove(activeObject)
        }

        return
      }

      if (
        e.keyCode === 32 &&
        !["input", "textarea"].includes(document.activeElement?.tagName.toLowerCase() || "") &&
        !this.panningKeyDown
      ) {
        this.canvasCursor = "grab"
        this.panningKeyDown = true
        this.canvas.discardActiveObject()
        this.objects.disableSelect()
        this.dispatchViewportState()

        return false
      }

      return true
    })

    document.addEventListener("keyup", () => {
      this.canvasCursor = "default"
      this.panningKeyDown = false

      if (!this.panningToggled) {
        this.objects.enableSelect()
      }

      this.dispatchViewportState()
    })

    this.canvas.upperCanvasEl.addEventListener("touchstart", e => {
      const initialZoom = 1
      if (this.objects.active || (isMobile() && initialZoom >= this.getZoom() && this.onViewport())) {
        this.stopPan()
        return
      }

      this.canvas.discardActiveObject()
      this.objects.disableSelect()
      this.lastPointerX = e.touches[0].clientX
      this.lastPointerY = e.touches[0].clientY
      this.startPan()
    })

    this.canvas.upperCanvasEl.addEventListener("touchend", e => {
      this.panningKeyDown = false
      this.dispatchViewportState()

      if (e.touches.length === 0) {
        this.objects.enableSelect()
      }
    })

    this.canvas.on("text:changed", e => {
      if (e.target) {
        this.objects.handleTextChanged(e.target)
      }
    })

    this.canvas.on("object:scaling", e => {
      if (e.target) {
        this.objects.handleScaling(e.target)
      }
    })

    this.canvas.on("object:rotating", e => {
      if (e.target) {
        this.objects.handleRotating(e.target)

        if (e.target.controls) {
          e.target.hasControls = false

          if (e.target.canvas) {
            const angle = (e.target.angle || 0) - this.lastObjectAngle

            e.target.canvas.setCursor(getRotateCursor(angle))
          }
        }
      }
    })

    this.canvas.on("object:moving", e => {
      if (e.target && this.objects.isSignObject(e.target)) {
        e.target.hasControls = false

        this.objects.keepWithinBounds(e.target)
        const result = this.objects.handleMoving(e.target)

        this.removeGuidelines()

        if (result) {
          this.addGuideLines(result.centerX, result.centerY, result.guidesX, result.guidesY)
        }
      }
    })

    this.canvas.on("object:modified", e => {
      if (e.target) {
        this.objects.handleModified(e.target)
        e.target.hasControls = true
        this.lastObjectAngle = e.target.angle || 0
      }

      this.objects.updateImages()
      this.store()
    })

    this.canvas.on("selection:created", e => {
      const target = e.selected && e.selected[0]

      if (this.drawing || !target) {
        return
      }

      this.objects.handleSelected(target)
    })

    this.canvas.on("selection:updated", () => {
      if (this.drawing) {
        return
      }

      this.stopPan()
      this.objects.updateActive()
    })

    this.canvas.on("selection:cleared", () => {
      if (this.drawing) {
        return
      }

      this.objects.updateActive()
    })

    this.canvas.on("mouse:dblclick", () => {
      this.onDoubleClickEvent.dispatch()
    })

    this.canvas.on("mouse:up", e => {
      if (this.panningKeyDown || this.panningToggled) {
        this.canvasCursor = "grab"
      }

      this.deleteEmptyObjects()

      if (e.target) {
        this.removeGuidelines()

        if (e.transform && e.transform.corner && this.objects.isSignObject(e.target)) {
          this.updateObjectPosition(e.target)
        }

        this.updateContourIfNeeded()
      } else {
        this.onEmptyClickEvent.dispatchAsync()
      }
    })

    this.canvas.on("mouse:move", () => {
      if (this.panningKeyDown || this.panning || this.panningToggled) {
        this.canvasCursor = "grab"
        return
      }
    })

    this.canvas.on("mouse:wheel", e => {
      const evt = e.e as WheelEvent

      if (macOs() || ios()) {
        if (!evt.metaKey) {
          return
        }
      } else if (!evt.ctrlKey) {
        return
      }

      const delta = -evt.deltaY

      let zoom = this.getZoom()
      zoom = zoom + delta / 200

      this.canvas.zoomToPoint(
        new fabric.Point(evt.offsetX, evt.offsetY),
        clamp(zoom, minZoom, maxZoom) * this.calcScale()
      )
      this.dispatchViewportState()
      this.redrawAfterZoom()

      if (this.activeObject) {
        this.updateObjectPosition(this.activeObject as SignObject)
      }

      evt.preventDefault()
      evt.stopPropagation()
    })

    this.canvas.on("text:editing:entered", e => {
      if (e.target) {
        const target = e.target as SignText

        if (target.isPlaceholder) {
          target.setSelectionStart(0)
          target.setSelectionEnd(0)
        }

        return this.onInlineEditEvent.dispatchAsync()
      }
    })

    if (ClientData.editing || ClientData.loadSignType || ClientData.exampleID) {
      this.preservePreviousSign()
      storageLocal.removeItem("sign_modified") // the initial loaded sign shouldn't be modified
    }

    info("loading fonts")
    Fonts.loadFonts(this.fontdata)

    info("loading state")

    if (storageLocal.getItem("update_sign") === "true") {
      storageLocal.removeItem("update_sign")

      if (storageLocal.getEncryptedItem("restorable_sign")) {
        this.restorePreviousSign()
        storageLocal.removeItem("restorable_sign")
      }
    } else {
      await this.restoreState(loadState)
    }
    this.updateState({}, true)
    this.updateScale(this.calcScale())

    info("loading images")
    this.objects.updateImages()

    info("loading canvas")
    this.contour.initCanvas(this.canvas)
    this.history.startRecording()

    info("draw loaded sign")
    window.addEventListener("resize", () => this.canvasResize())
    new ResizeSensor(this.canvasWrapper, () => this.canvasResize())
    this.draw()

    this.triggerOnLoad()
  }

  /**
   * Restore state from localStorage or global variable.
   *
   * Precedence:
   * 1. If global variable `loadSign` exists then it's either considered a v2 state and converted
   *  or v3 state and loaded. This is used for loading shared sign.
   * 2. If `sign_convert` key exists in localstorage then it's considered a v1 state and converted.
   * 3. If `sign` key exists in localstorage then it's either considered a v2 state and converted or
   *  v3 state and loaded.
   */
  restoreState(loadState?: any): Promise<void> {
    return new Promise(resolve => {
      let storedState = loadState
      const storedType = ClientData.loadSignType

      if (!loadState) {
        if (ClientData.loadSign) {
          storedState = ClientData.loadSign

          if (ClientData.woodEngraved) {
            storageLocal.setItem("signType", "plastic_sign")
          } else if (!ClientData.editing) {
            storageLocal.removeItem("signType")
          }
        } else {
          const legacySign = ClientData.loadSignConvert || storageLocal.getEncryptedItem("sign_convert")

          if (legacySign) {
            storedState = SigncodeConverter.v1(legacySign, this.pixelRatio, 1)
            storageLocal.removeItem("sign_convert")

            const v1alert = document.getElementById("converted-sign-alert")

            if (v1alert) {
              v1alert.classList.remove("hidden")
              setTimeout(() => v1alert.classList.add("hidden"), 10000)
            }
          } else {
            const decrypted = storageLocal.getEncryptedItem("sign")

            if (decrypted) {
              storedState = JSON.parse(decrypted)
            }
          }
        }

        // No saved state, set default state
        if (!storedState) {
          this.setState()
          return resolve()
        }
      }

      const showWoodAlert = ClientData.woodEngraved
      const woodalert = document.getElementById("engraved-wood-alert")

      if (woodalert && showWoodAlert) {
        woodalert.classList.remove("hidden")
        setTimeout(() => woodalert.classList.add("hidden"), 20000)
      }

      const showWoodSizeTypeAlert = ClientData.woodTypeAlert && ClientData.woodSizeAlert
      const woodSizeTypeAlert = document.getElementById("wood-size-type-unavailable-alert")
      const showWoodTypeAlert = ClientData.woodTypeAlert
      const woodTypeAlert = document.getElementById("wood-type-unavailable-alert")
      const showWoodSizeAlert = ClientData.woodSizeAlert
      const woodSizeAlert = document.getElementById("wood-size-unavailable-alert")

      if (woodSizeTypeAlert && showWoodSizeTypeAlert) {
        woodSizeTypeAlert.classList.remove("hidden")
        setTimeout(() => woodSizeTypeAlert.classList.add("hidden"), 20000)
      } else if (woodTypeAlert && showWoodTypeAlert) {
        woodTypeAlert.classList.remove("hidden")
        setTimeout(() => woodTypeAlert.classList.add("hidden"), 20000)
      } else if (woodSizeAlert && showWoodSizeAlert) {
        woodSizeAlert.classList.remove("hidden")
        setTimeout(() => woodSizeAlert.classList.add("hidden"), 20000)
      }

      if (!this.signType) {
        throw new Error("Signomatic sign type should be set by now")
      }

      let switched
      const previousType = storageLocal.getItem("signType")
      if (!storedType && previousType && previousType !== this.signType && !ClientData.editing) {
        switched = this.signType
      }

      info(`switched: ${switched}`)

      const admin = !!(ClientData.admin && ClientData.admin.id)
      const editing = ClientData.editing
      const defaults = defaultState(this.signType, this.metaProduct, this.data, switched)
      const state: SignState = SigncodeConverter.isV3(storedState)
        ? storedState
        : SigncodeConverter.v2(storedState, defaults, this.centerX, this.centerY)

      const metaProducts = ClientData.metaProducts || []

      if (ClientData.minimaticData?.color) {
        state.color = ClientData.minimaticData.color
      }

      if (ClientData.minimaticData?.contentColor) {
        state.contentColor = ClientData.minimaticData.contentColor
      }

      if (switched && !metaProducts.includes(switched)) {
        state.metaProduct = undefined
        state.metaType = undefined
      }

      if (state.metaProduct && state.metaType) {
        this.signType = state.metaType
      }

      if (this.metaProduct) {
        state.metaProduct = this.metaProduct
      }

      if (editing && editing.admin && admin) {
        state.fixedPrice = true
      }

      // legacy option data adjustments
      if (storedType && !ClientData.exampleID) {
        if (this.signType === "vinyl_text") {
          const currentMin = currentMinText(this.originalData, state)

          for (const obj of state.objects) {
            if (Sign.isText(obj) && obj.size < currentMin) {
              this.data.min_text = [{ size: obj.size }, ...this.data.min_text]
            }
          }
        }

        if (this.signType === "plastic_sign") {
          if (state.thickness === 0.8 && (state.width > 150 || state.height > 100)) {
            state.thicknessLegacySizes = true
          }

          if (state.width > this.originalData.max_size.w) {
            this.data.max_size.w = state.width
            info(`overriding max width to ${state.width}`)
          }

          if (state.width < this.originalData.min_size.w) {
            this.data.min_size.w = state.width
            info(`overriding min width to ${state.width}`)
          }
        }

        if (this.signType === "magnetic_sign") {
          if (state.width > this.originalData.max_size.w) {
            this.data.max_size.w = state.width
          }
        }

        if (this.signType === "name_sign") {
          if (
            state.color === "#e8cca1" &&
            state.borderColor === "#8c4a20" &&
            !(this.data.fixed_colors as ColorSet[]).some(c => c[0] === "#e8cca1")
          ) {
            ;(this.data.fixed_colors as ColorSet[]).push(["#e8cca1", "#8c4a20"])
            state.contentColor = "#8c4a20"
          }
        }

        if (state.badgeName === "stainless_28x49") {
          state.shape = "arched_top_bottom"
        }

        if (this.signType === "wood_sign" && state.shape == "thorny") {
          const shapeData = this.originalData.shapes.filter(shape => shape.name == state.shape)[0]
          if (shapeData) {
            const min_size = shapeData.filters?.min_size
            if (min_size) {
              state.width = Math.max(min_size[0].w, state.width)
              state.height = Math.max(min_size[0].h, state.height)
            }
          }
        }

        const fonts = this.data.fonts?.map(f => f.toLowerCase()) || []
        const fontAlert = document.getElementById("unsupported-font-style-alert")
        let showFontAlert = false

        for (const obj of state.objects) {
          if (Sign.isText(obj)) {
            // restore legacy fonts
            if (Fonts.fontExists(obj.font.toLowerCase())) {
              Fonts.allowFont(obj.font.toLowerCase())

              if (!fonts.includes(obj.font.toLowerCase())) {
                this.data.fonts?.push(obj.font)
              }
            }

            // reset styles font doesn't allow
            const allowedStyles = Fonts.allowedStyles(obj.font, this.fontdata)

            if (!allowedStyles.boldItalic && obj.bold && obj.italic) {
              showFontAlert = true
              obj.bold = false
              obj.italic = false
            }

            if (!allowedStyles.bold && obj.bold) {
              showFontAlert = true
              obj.bold = false
            }

            if (!allowedStyles.italic && obj.italic) {
              showFontAlert = true
              obj.italic = false
            }
          }
        }

        if (showFontAlert && fontAlert) {
          fontAlert.classList.remove("hidden")
          setTimeout(() => fontAlert.classList.add("hidden"), 20000)
        }
      } else if (showWoodAlert && this.signType === "color_badge") {
        let color = ""
        let shape = ""

        switch (state.color) {
          case "#eed895":
            color = "birch"
            break
          case "#e8cca1":
            color = "oak"
            break
          case "#be9261":
            color = "cherry"
            break
          default:
            color = "birch"
            break
        }

        switch (state.shape) {
          case "rect":
          case "rounded":
            shape = "round"
            break
          case "ellipse":
            shape = state.width === state.height ? "ellipse_oval" : "ellipse"
            break
          case "bulge":
            shape = "bulge"
            break
          default:
            shape = "round"
            break
        }

        state.preset = ["badge", color, shape].join("_")

        if (state.fixmethod === "military") {
          state.preset += "_military"
        }
      } else if (this.signType === "wood_sign" && showWoodTypeAlert) {
        state.preset = "wood_birch"
      } else if (state.badgeName === "stainless_28x49") {
        state.shape = "arched_top_bottom"
      }

      this.exampleID = ClientData.exampleID || storedState.exampleID
      this.recommended = ClientData.recommended || ""
      this.signCategoryID = ClientData.signCategoryID || storedState.signCategoryID
      this.examplerID = ClientData.examplerID || storedState.examplerID

      if (this.metaProduct) {
        this.onMetaSignTypeChangedEvent.dispatch({ metaProduct: this.metaProduct || "", signType: this.signType })
      }

      info("setting loaded state")
      const objects = [...state.objects]
      this.setState({ ...state, objects: [] }, switched)

      const objectRestoreTimeout = env.isJSDOM ? 3000 : 10000

      const timeout = () => {
        error("timed out waiting for objects to restore!")
        resolve()
      }

      const finishTimeout = new Promise((_, reject) => {
        const wait = setTimeout(() => {
          clearTimeout(wait)
          reject()
        }, objectRestoreTimeout)
      })

      info("restoring objects")
      Promise.race([finishTimeout, this.objects.restore(objects, this.centerX, this.centerY, this.fontdata)]).then(
        () => resolve(),
        () => timeout()
      )
    })
  }

  preservePreviousSign() {
    // we should update restorable sign if the new one has changed
    if (storageLocal.getEncryptedItem("restorable_sign") && !storageLocal.getItem("sign_modified")) {
      return
    }

    // we don't need to save the restorable sign because it hasn't been modified
    if (!storageLocal.getItem("sign_modified")) {
      return
    }

    const previousSign = storageLocal.getEncryptedItem("sign")

    // user has no previous sign
    if (!previousSign) {
      return
    }

    const previousState = JSON.parse(previousSign)

    // previous sign must be a v3 signstate
    if (!SigncodeConverter.isV3(previousState)) {
      return
    }

    const previousMetaType = storageLocal.getItem("metaProduct")
    const previousType = storageLocal.getItem("signType")

    // we have a previous sign but we didn't save the type? maybe user was editing
    if (!previousType) {
      return
    }

    const previousSignData: PreviousSignData = {
      state: previousState,
      metaProduct: previousMetaType,
      signType: previousType,
    }

    storageLocal.setEncryptedItem("restorable_sign", JSON.stringify(previousSignData))
  }

  restorePreviousSign() {
    const previousData = storageLocal.getEncryptedItem("restorable_sign")

    if (!previousData) {
      return
    }

    const { state, metaProduct, signType }: PreviousSignData = JSON.parse(previousData)
    this.removePreviousSignData()

    if (metaProduct) {
      this.setMetaSignType(metaProduct, signType)
    } else {
      this.setSignType(signType)
    }

    this.restoreState(state)

    return { metaProduct, signType }
  }

  removePreviousSignData() {
    storageLocal.removeItem("restorable_sign")
    storageLocal.removeItem("sign_modified")
  }

  /**
   * Switch sign type
   */
  setSignType(type: string) {
    if (type === this.signType && !this.state?.metaProduct) {
      return
    }

    if (!this.signTypes.includes(type)) {
      throw new Error("Unknown sign type")
    }

    const metaProducts = ClientData.metaProducts || []
    if (metaProducts.includes(type)) {
      throw new Error(`Can't set metaProduct - ${type} via setSignType`)
    }

    // Set new type and revalidate state
    const initial = this.signType === null
    const switched = this.signType
    this.signType = type
    this.metaProduct = undefined
    this.exampleID = null
    this.signCategoryID = null
    this.examplerID = null
    this.signData = cloneDeep(this.originalDatas)
    this.data.fonts = Fonts.enabledFonts(type, this.fontdata, this.data.font_set || "")

    if (!initial) {
      this.setState({ ...this.state, metaProduct: undefined, metaType: undefined }, switched)
      this.resetZoomAndPan()
    }

    this.history.clear()
    document.querySelector("body")?.setAttribute("signtype", type)
    this.onSignTypeChangedEvent.dispatch(type)
  }

  setMetaSignType(metaProduct: string, signType?: string) {
    if (signType === this.signType) {
      return
    }

    if (!signType) {
      const storedType: string = ClientData.loadSignType
      const signTypeFromLocalStorage = storageLocal.getItem("signType")
      const signData = this.originalDatas.find(data => data.name === metaProduct)

      const enabledSignTypes = signData?.signtypes || []

      if (storedType && enabledSignTypes.includes(storedType)) {
        signType = storedType
      } else if (signTypeFromLocalStorage && enabledSignTypes.includes(signTypeFromLocalStorage)) {
        signType = signTypeFromLocalStorage
      } else if (this.state?.metaType && enabledSignTypes.includes(this.state?.metaType)) {
        signType = this.state?.metaType
      } else if (signTypeFromLocalStorage === "aluminium_sign" && metaProduct === "meta_plastic_sign") {
        signType = "solid_plastic_sign"
      } else {
        signType = enabledSignTypes[0]
      }

      if (
        metaProduct == "meta_name_sign" &&
        this.state?.preset &&
        ["label_anodised_aluminium"].includes(this.state?.preset)
      ) {
        signType = "etched_aluminium_sign"
      }
    }

    const metaProducts = ClientData.metaProducts || []
    if (!metaProducts.includes(metaProduct)) {
      throw new Error(`Unknown meta product ${metaProduct}`)
    }

    // Set new type and revalidate state
    const initial = this.signType === null
    const switched = signType
    this.signType = signType
    this.metaProduct = metaProduct
    this.exampleID = null
    this.signCategoryID = null
    this.examplerID = null
    this.signData = cloneDeep(this.originalDatas)
    this.data.fonts = Fonts.enabledFonts(signType, this.fontdata, this.data.font_set || "")

    if (!initial) {
      const update: Partial<SignState> = { metaProduct: metaProduct, metaType: signType }
      this.setState({ ...this.state, ...update }, switched, update)
      this.resetZoomAndPan()
    }

    this.history.clear()
    document.querySelector("body")?.setAttribute("signtype", metaProduct)
    this.onMetaSignTypeChangedEvent.dispatch({ metaProduct, signType })
  }

  /**
   * Validates and corrects received state, and sets it to current state
   */
  setState(
    state?: SignState,
    switched?: string | null,
    update?: Partial<SignState>,
    draw?: boolean,
    skipContour?: boolean
  ) {
    if (!this.signType) {
      info("sign type not set yet")
      return
    }

    const initialZoom = this.state ? this.getZoom() : 1
    const initialWidth = this.state?.width || 0
    const initialHeight = this.state?.height || 0

    const data = cloneDeep(this.data)

    this.state = state
      ? correctState(state, this.signType, data, switched, update, this.signCLI, this.fontdata)
      : defaultState(this.signType, this.metaProduct, data, switched)

    if (!state) {
      this.objects.clear()
    }

    this.objects.syncFromState(this.state)

    const contour = this.signType === "sticker" && this.state.shape === "contour" && this.objects.length === 1

    for (const obj of this.objects.all()) {
      obj.setControlVisible("mtr", !contour)
    }

    if (this.state.widthAuto || this.state.heightAuto) {
      this.state = this.objects.autoSize()
      draw = true

      if (this.state.width >= this.data.max_size.w || this.state.height >= this.data.max_size.h) {
        for (const obj of this.objects.all()) {
          this.objects.keepWithinBounds(obj)
        }
      }
    } else if (!this.state.widthAuto && !this.state.heightAuto) {
      for (const obj of this.objects.all()) {
        this.objects.keepWithinBounds(obj)
      }
    }

    if (typeof draw === "undefined" || draw) {
      this.draw()
    }

    if (this.state.shape === "contour" && !skipContour) {
      this.updateContourIfNeeded()
    }

    this.calcState(this.state)

    if (
      this.state.width !== initialWidth ||
      this.state.height !== initialHeight ||
      (initialZoom === 1 && initialZoom !== this.getZoom())
    ) {
      this.resetZoomAndPan()
    }

    this.store()

    if (this.history.unsavedChanges || switched) {
      this.storeModified()
    }

    this.dispatchStateEvents(update)
    this.evaluateStateTesters()
  }

  /**
   * Amends part of the current state
   */
  updateState(update: Partial<SignState>, draw?: boolean, skipHistory?: boolean) {
    if (Object.prototype.hasOwnProperty.call(update, "side")) {
      throw new Error("Please set sign side via setSide")
    }

    if (this.signType === "label_plates") {
      const material = update.material || this.state.material
      this.data.fonts = Fonts.enabledFonts(this.signType, this.fontdata, this.data.font_set || "", material)
    }

    if (!skipHistory) {
      this.history.recordPast(this.state, update)
    }

    this.setState({ ...this.state, ...update }, null, update, draw)
  }

  undo() {
    this.history.undo()
  }

  redo() {
    this.history.redo()
  }

  objectPaste(state: SignObjectState) {
    this.history.recordObjectAdded(this.state)

    this.duplicateObject(state, this.centerX, this.centerY)
  }

  changesCommited() {
    this.history.changesCommited()
  }

  setPreset(name: string) {
    const presets = allPresets(this.data)
    let preset = presets.find(item => item.name === name)

    if (preset && preset.group_alias) {
      const group = (this.data.preset_groups || {})[preset.name]

      if (group) {
        preset = (group.presets || [])[0]
      }
    }

    if (preset && preset.settings) {
      const previousDefaultColor = this.fullData.default_color
      let presetState = applyPreset(this.state, preset)

      if (this.state.material === "wood" && previousDefaultColor) {
        const newDefaultColor = dataWithPreset(this.fullData, { ...this.state, ...presetState }).default_color

        if (newDefaultColor) {
          const borderColor = this.state.borderColor === previousDefaultColor ? newDefaultColor : this.state.borderColor

          for (const obj of this.state.objects) {
            if (obj.color === previousDefaultColor) {
              obj.color = newDefaultColor
            }

            if (obj.border && obj.border.borderColor === previousDefaultColor) {
              obj.border.borderColor = newDefaultColor
            }
          }
          presetState = { ...presetState, borderColor, objects: this.state.objects }
        }
      }

      this.updateState(presetState)
    }
  }

  setSide(side: number, simple?: boolean) {
    if (side === this.state.side) {
      return
    }

    if (side === 1 && !simple) {
      this.updateState({ seenBside: true }, false)
    }

    if (ie()) {
      simple = true
    }

    clearTimeout(this.flipCleanup)

    for (const old of Array.from(this.canvasWrapper.querySelectorAll(".old-side"))) {
      old.remove()
    }

    // reset
    this.canvasWrapper.classList.remove("flip")
    this.canvasWrapper.classList.remove("flip-active")
    this.canvasWrapper.classList.remove("flip-reverse")

    if (simple || env.TEST) {
      this.setState({ ...this.state, side }, null, { side }, false)
      this.objects.handleSideChange()
      this.draw()
      return
    }

    // create new canvas and copy current contents to it
    const newCanvas = document.createElement("canvas")
    const context = newCanvas.getContext("2d")

    if (!context) {
      throw new Error("Unable to construct canvas")
    }

    newCanvas.classList.add("old-side")
    newCanvas.width = this.canvas.getElement().width
    newCanvas.height = this.canvas.getElement().height

    context.drawImage(this.canvas.getElement(), 0, 0)
    this.canvasWrapper.appendChild(newCanvas)

    // flip sides and draw new side
    this.canvasWrapper.classList.add("flip")

    this.setState({ ...this.state, side }, null, { side }, false)
    this.objects.handleSideChange()
    this.draw()

    // animate
    if (side === 0) {
      this.canvasWrapper.classList.add("flip-reverse")
    }

    setTimeout(() => this.canvasWrapper.classList.add("flip-active"), 10)

    // cleanup
    this.flipCleanup = window.setTimeout(() => {
      this.canvasWrapper.classList.remove("flip")
      this.canvasWrapper.classList.remove("flip-active")
      this.canvasWrapper.classList.remove("flip-reverse")
      newCanvas.remove()
    }, 1500)
  }

  copySide() {
    const toAdd = []
    const toRemove = []

    for (const obj of this.objects.all()) {
      if (obj.state.side === 0) {
        const clone = this.objects.cloneObject(obj.state, this.centerX, this.centerY)

        if (clone) {
          clone.state.side = 1
          clone.isPlaceholder = false
          toAdd.push(clone)
        }
      } else {
        toRemove.push(obj)
      }
    }

    const historyState = cloneDeep(this.state)

    for (const obj of toAdd) {
      this.objects.add(obj)
    }

    for (const obj of toRemove) {
      this.objects.remove(obj, true)
    }

    const sideState = cloneDeep(this.state.sideState) || []
    invariant(sideState[0])
    sideState[1] = {
      border: sideState[0].border,
      color: sideState[0].color,
      borderColor: sideState[0].borderColor,
    }

    let update: Partial<SignState> = { sideState }

    if (this.state.side === 1) {
      update = {
        ...update,
        border: sideState[0].border,
        color: sideState[0].color,
        borderColor: sideState[0].borderColor,
      }
    }

    this.history.recordCopySide(historyState, update, toRemove)
    this.updateState(update, this.state.side === 1, true)
  }

  async createObject(type: SignObjecType, state?: Partial<SignObjectState>) {
    this.history.recordObjectAdded(this.state)

    const obj = await this.objects.create(type, state)

    if (obj) {
      this.canvas.setActiveObject(obj)

      if (!isTablet() && Sign.isTextObj(obj)) {
        obj.enterEditing()
      }

      this.canvas.renderAll()
      return obj
    }

    return null
  }

  getObject(state: SignObjectState) {
    return this.objects.get(state)
  }

  deleteObject(state: SignObjectState) {
    const obj = this.objects.get(state)

    if (obj) {
      this.objects.remove(obj)
    }
  }

  deleteEmptyObjects() {
    const texts = this.objects.texts

    texts.forEach(textObj => {
      if (textObj.state.text.trim() == "") {
        this.objects.remove(textObj)
      }
    })
  }

  deletePlaceholderTextObjects() {
    const texts = this.objects.texts

    texts.forEach(textObj => {
      if (textObj.isPlaceholder) {
        this.objects.remove(textObj)
      }
    })
  }

  duplicateObject(state: SignObjectState, centerX: number, centerY: number) {
    this.history.recordObjectAdded(this.state)

    const clone = this.objects.cloneObject(state, centerX, centerY) as SignObject
    clone.state.side = this.state.side
    clone.isPlaceholder = false
    this.objects.add(clone)
    let referenceObject: SignObject | undefined = undefined

    if (this.activeObject) {
      referenceObject = this.activeObject as SignObject
    } else {
      referenceObject = clone
    }

    this.setObjectActive(clone.state)

    this.updateObjectState({
      left: referenceObject.state.left + this.state.width / 10,
      top: referenceObject.state.top + this.state.width / 10,
    })

    this.arrangeObject(clone.state, "front")
    this.updateObjectPosition(clone)
  }

  objectRendered(obj?: fabric.Object | null) {
    if (!obj) {
      return false
    }

    return this.canvas.getObjects().includes(obj)
  }

  setObjectActive(state: SignObjectState | null) {
    const obj = state && this.objects.get(state)

    if (!state || !this.objectRendered(obj)) {
      this.canvas.discardActiveObject()
      this.canvas.renderAll()
      return
    }

    if (obj) {
      this.canvas.setActiveObject(obj)
      this.canvas.renderAll()
    }
  }

  resetObjectState() {
    this.objects.resetActiveState()
  }

  updateObjectState(state: Partial<SignObjectState>) {
    this.objects.updateActiveState(state, this.fontdata)

    if (state.left === 0 || state.top === 0) {
      this.updateObjectPosition(this.activeObject as SignObject)
    }
  }

  updateExactObjectState(obj: SignObject, state: Partial<SignObjectState>) {
    this.objects.updateState(obj, state, true)
  }

  updateObjectBorder(state: Partial<SignObjectBorder> | null) {
    this.objects.updateObjectBorder(state)
  }

  arrangeObject(state: SignObjectState, direction: "front" | "forward" | "backward") {
    this.objects.arrangeObject(state, direction)
  }

  restoreObjectInstance(obj: SignObject) {
    this.objects.restoreObjectInstance(obj)
  }

  clipShape(printableArea = false, offsetX = 0, offsetY = 0): DrawableObject {
    const printAreaPadding = Calc.mmToPx(this.printAreaPadding())
    let drawable
    if (printableArea && printAreaPadding > 0) {
      drawable = this.drawables.printAreaCutlineDrawable()

      if (drawable) {
        if (drawable instanceof fabric.Group) {
          drawable = drawable.getObjects()[0] as DrawableMixin
        }

        drawable.set({
          name: "clip",
          absolutePositioned: true,
          left: this.centerX + (drawable.left || 0) + printAreaPadding,
          top: this.centerY + (drawable.top || 0) + printAreaPadding,
        })
      }
    } else {
      drawable = this.drawables.cutlineDrawable(true)

      if (drawable) {
        if (drawable instanceof fabric.Group) {
          drawable = drawable.getObjects()[0] as DrawableMixin
        }

        drawable.set({
          name: "clip",
          absolutePositioned: true,
          left: this.centerX + offsetX,
          top: this.centerY + (drawable.top || 0) + offsetY,
        })
      }
    }

    return drawable
  }

  currentBase(): fabric.Object | null {
    for (const obj of this.canvas.getObjects()) {
      if (obj instanceof fabric.Group) {
        for (const groupobj of obj.getObjects() as DrawableMixin[]) {
          if (groupobj.classes && groupobj.classes.includes("base")) {
            return obj
          }
        }
      }

      if ((obj as DrawableMixin).classes?.includes("base")) {
        return obj
      }
    }

    return null
  }

  redraw() {
    this.draw()
  }

  rerender() {
    this.canvas.renderAll()
  }

  /**
   * Persists state for reloads
   */
  store = debounce(() => this.storeImmediate(), 300, { leading: true, trailing: true })
  storeModified = debounce(() => this.storeModifiedImmediate(), 300, { leading: true, trailing: true })

  /**
   * Persist state synchronously
   */
  storeImmediate(state?: SignState, minimaticCustomise?: boolean) {
    if (this.clearing || !this.loaded || !this.signType || (this.minimatic && !minimaticCustomise)) {
      return
    }

    info("storing sign")
    storageLocal.setEncryptedItem("sign", JSON.stringify(state || this.state))

    if (!ClientData.editing) {
      storageLocal.setItem("metaProduct", this.metaProduct || "")
      storageLocal.setItem("signType", this.signType)
    }
  }

  storeModifiedImmediate() {
    if (this.clearing || !this.loaded || !this.signType || this.minimatic) {
      return
    }

    info("storing modified")
    storageLocal.setItem("sign_modified", "true")
  }

  async clear() {
    this.clearing = true
    document.querySelector("body")?.classList.remove("signomaticloaded", "minimaticloaded")
    storageLocal.removeItem("sign")
    storageLocal.removeItem("sign_modified")

    if (this.onClearHandlers.length > 0) {
      for (const handler of this.onClearHandlers) {
        await handler()
      }
    }

    document.location.reload()
  }

  async toSVG(simple = false): Promise<string[]> {
    if (simple) {
      return ["<svg></svg>"]
    }

    const result: string[] = []

    if (!this.state.doubleSided) {
      result.push(await this.export.SVG())
      return result
    }

    const side = this.state.side || 0

    this.setSide(0, true)
    result.push(await this.export.SVG())

    this.setSide(1, true)
    result.push(await this.export.SVG())
    this.setSide(side, true)

    return result
  }

  toEngravingSVG(debug = false, simple = false): Promise<string> {
    if (this.production !== "laser") {
      return Promise.resolve("")
    }

    if (simple) {
      return Promise.resolve("<svg></svg>")
    }

    return this.export.engravingSVG(debug)
  }

  async signcode(simple = false) {
    if (!this.canvas) {
      return null
    }

    const svg = await this.toSVG(simple)
    const skyltprintSVG = await this.toEngravingSVG(false, simple)
    const json: SignStateV2 | Record<string, never> = simple ? {} : this.toJSON()
    const basketId = simple ? 0 : Math.round(Math.random() * 99999999)
    const price = simple ? {} : this.priceData()

    const material = this.state.material
    const materialName = ["regular", null].includes(material) ? null : i18n.t(`material_type.${material}`)

    return {
      version: "2.14",
      sign_type: this.signType,
      v_text: this.objects.textContent(),
      v_hang_type: this.state.fixmethod,
      v_sign_style: this.state.shape,
      v_sign_border: this.state.border,
      v_background_color: this.state.color || "#f0f0f0",
      v_text_color: this.state.contentColor,
      v_border_color: this.state.borderColor,
      reflective: this.state.material === "reflective",
      material,
      material_name: materialName,
      count: 1,
      basket_id: basketId,
      height: this.state.forcedHeight || this.state.height,
      width: this.state.forcedWidth || this.state.width,
      full_height: this.state.height,
      full_width: this.state.width,
      description: this.objects.textDescription(),
      sign_type_name_tr: i18n.t(`sign_type.${this.signType}`),
      hang_type_name_tr: this.state.fixmethod ? i18n.t(`fixmethod.${this.state.fixmethod}`) : null,
      images: this.objects.imageParams(),
      skyltprint_svg: skyltprintSVG,
      fabric_svg: svg[0],
      fabric_svg_b: svg[1],
      fabric_json: json,
      example_id: this.exampleID,
      recommended: this.recommended,
      sign_category_id: this.signCategoryID,
      exampler_id: this.examplerID,
      badge_name: this.state.badgeName,
      double_sided: this.state.doubleSided,
      side_state: this.state.sideState,
      opentype: this.useOpentype,
      fixmethod_count: this.drawables.fixmethodCount,
      pdfpadding: this.drawables.printPaddingWidth,
      meta_product: this.state.metaProduct,
      thickness: this.state.thickness,
      external_fixmethod_padding: this.externalFixmethodPadding(),
      ...price,
    }
  }

  onClear(handler: ClearHandler) {
    this.onClearHandlers.push(handler)
  }

  onClearRemove(handler: ClearHandler) {
    this.onClearHandlers = this.onClearHandlers.filter(h => h === handler)
  }

  async waitImagesLoaded() {
    await this.drawables.waitImagesLoaded()
  }

  async generateSign(contents: string[]): Promise<string> {
    this.disableDraw = true
    this.canvas.renderOnAddRemove = false
    this.importContents(contents)

    if (this.state.shape === "contour") {
      this.updateContour()
    }

    return JSON.stringify(await this.signcode())
  }

  updateContour() {
    info("updating contour")

    const contourData = this.contour.createPath()

    if (contourData) {
      this.setState({ ...this.state, contourData }, null, { contourData }, true, true)
    }
  }

  importContents(contents: string[]) {
    this.objects.positionTexts(contents)
  }

  fixmethodPadding() {
    return this.drawables.fixmethodPadding()
  }

  connectStore(store: Store) {
    return new StateBridge(store, this)
  }

  exitInlineEditing() {
    this.objects.exitEditing()
  }

  /**
   * Calculates optimal zoom for given sign size
   */
  calcScale(state?: SignState, canvasWidth?: number, canvasHeight?: number): number {
    canvasWidth = canvasWidth || this.canvas.getWidth()
    canvasHeight = canvasHeight || this.canvas.getHeight()

    state = state || this.state
    const fixmethodTop = this.drawables.externalTopPadding
    const fixmethodBottom = this.drawables.externalBottomPadding
    let fixmethodHeightPadding = Math.max(fixmethodTop, fixmethodBottom)
    const fixmethodLeft = this.drawables.externalLeftPadding
    const fixmethodRight = this.drawables.externalRightPadding
    let fixmethodWidthPadding = Math.max(fixmethodLeft, fixmethodRight)
    const padding = env.isJSDOM ? 0 : Math.max(state.widthPx, state.heightPx) / (isTablet() ? 4 : 16)

    if (!this.visualClipping) {
      fixmethodHeightPadding = Math.max(
        fixmethodHeightPadding,
        this.drawables.visualPadding.top,
        this.drawables.visualPadding.bottom
      )
      fixmethodWidthPadding = Math.max(
        fixmethodWidthPadding,
        this.drawables.visualPadding.left,
        this.drawables.visualPadding.right
      )
    }

    const width = state.widthPx + Math.max(fixmethodWidthPadding * 2 + 10, padding * 2)
    const height = state.heightPx + Math.max(fixmethodHeightPadding * 2 + 10, padding * 2)
    const signRatio = width / height
    const canvasRatio = canvasWidth / canvasHeight
    const scale = signRatio > canvasRatio ? canvasWidth / width : canvasHeight / height

    return Math.max(parseFloat(scale.toFixed(3)), 0.001)
  }

  /**
   * Test helper that registers a test function that is called when state is updated and if it
   * returns true the specified css class is added to body element.
   */
  addStateTest(test: () => boolean, cssclass: string) {
    this.stateTesters.push([test, cssclass])
    this.evaluateStateTesters()
  }

  /**
   * Resize canvas to real size of current sign
   */
  resizeCanvasToSignScale(scale = 1) {
    const width = this.state.forcedWidth ? Math.round(Calc.mmToPx(this.state.forcedWidth)) : this.state.widthPx
    let height = this.state.forcedHeight ? Math.round(Calc.mmToPx(this.state.forcedHeight)) : this.state.heightPx

    if (this.drawables.visualPadding.top > 0) {
      height = height + this.drawables.visualPadding.top
    }

    if (this.drawables.visualPadding.bottom > 0) {
      height = height + this.drawables.visualPadding.bottom
    }

    this.canvasResizeImmediate(width * scale, height * scale)
    this.canvas.renderAll()
  }

  togglePan() {
    this.panningToggled = !this.panningToggled

    if (this.panningToggled) {
      this.objects.disableSelect()
    } else if (!this.panningKeyDown) {
      this.objects.enableSelect()
    }

    this.dispatchViewportState()
  }

  resetZoomAndPan() {
    this.updateScale()

    if (this.activeObject) {
      this.updateObjectPosition(this.activeObject as SignObject)
    }
  }

  updateScale(scale?: number) {
    const newScale = scale || this.calcScale()
    const original = this.scale

    if (original !== newScale) {
      info("zooming from " + original + " to " + newScale)
    }

    this.canvas.zoomToPoint(new fabric.Point(this.centerX, this.centerY), newScale)
    this.resetViewport()

    if (!this.signCLI && !this.minimatic) {
      this.dimensions.redrawDimensions(this)
    }
  }

  getZoom() {
    return this.canvas.getZoom() / this.calcScale()
  }

  /** Set zoom relative to current pan center */
  setZoom(zoom: number) {
    this.canvas.zoomToPoint(
      new fabric.Point(this.centerX, this.centerY),
      this.calcScale() * clamp(zoom, minZoom, maxZoom)
    )
    this.dispatchViewportState()
    this.redrawAfterZoom()

    if (this.activeObject) {
      this.updateObjectPosition(this.activeObject as SignObject)
    }
  }

  printAreaPadding() {
    let padding = 0

    if (this.signType == "brass_sign") {
      switch (this.state.badgeName) {
        case "london":
          padding = 2.4
          break
        case "oslo":
          padding = 2.6
          break
        case "paris":
          padding = 14.5
          break
        case "stockholm":
          padding = 4.1
          break
        case "berlin":
        case "copenhagen":
        case "athens":
        case "amsterdam":
        case "tallinn":
          padding = 1.7
          break
        case "sydney":
        case "lisbon":
          padding = 1.4
          break
        case "rome":
          padding = this.state.preset == "rome_brass_blank" ? 2.2 : 1.7
          break
        case "dublin":
          padding = 6
          break
      }
    } else if (this.signType == "color_badge" && ["brass", "nickel", "aluminium"].includes(this.state.material)) {
      if (this.state.shape == "bulge") {
        padding = 2
      } else if (
        this.state.preset &&
        ["badge_brass_ellipse", "badge_nickel_ellipse", "badge_aluminium_ellipse"].includes(this.state.preset)
      ) {
        padding = 4
      }
    }

    return padding
  }

  minimaticExtraPadding(textObj: SignText) {
    return calcMinimaticExtraPadding(
      this.state,
      textObj,
      this.signType,
      this.drawables.borderWidth,
      this.drawables.retroParams()
    )
  }

  minimaticText(text: string, originalSize: number, text2Size?: number, text3Size?: number) {
    const objs = this.allObjects.filter(obj => Sign.isText(obj.state)) as SignText[]
    const textObject = this.activeObject as SignText

    if (!textObject) {
      return
    }

    const retroParams = this.drawables.retroParams()

    const availableSpace = (textObj: SignText) => {
      const alignmentPadding =
        textObj.state.justify === "center"
          ? 0
          : textObj.state.justify === "right"
            ? this.state.widthPx / 2 - textObj.state.left
            : this.state.widthPx / 2 + textObj.state.left
      const spaceCentered =
        (this.state.widthPx / 2 - Math.abs(textObj.state.left)) * 2 -
        calcMinimaticExtraPadding(this.state, textObj, this.signType, this.drawables.borderWidth, retroParams)
      const spaceNotCentered =
        this.state.widthPx -
        alignmentPadding -
        calcMinimaticExtraPadding(this.state, textObj, this.signType, this.drawables.borderWidth, retroParams)

      if (textObj.state.justify === "center") {
        return spaceCentered
      } else {
        return spaceNotCentered
      }
    }

    const maxSize = (textObj: SignText) => {
      const rect = textObj.getBoundingRect(true, true)
      const sizeFactor = rect.width / textObj.state.size
      const maxSize = availableSpace(textObj) / sizeFactor

      return maxSize
    }

    const keepSmallerThanTitle = (textSize: number, obj: SignText) => {
      if (objs[0].state.size < obj.state.size) {
        obj.state.size = objs[0].state.size
      } else {
        obj.state.size = Math.min(textSize, maxSize(obj), objs[0].state.size)
      }

      this.updateObjectState({ size: size })
      obj.state.width = obj.getScaledWidth()
    }

    this.updateObjectState({ text: text })

    const updatedTextObject = this.activeObject as SignText

    let size = Math.min(maxSize(updatedTextObject), originalSize)
    if (metaNameSign(this.signType || "") && (this.activeObject === objs[1] || this.activeObject === objs[2])) {
      size = Math.min(objs[0].state.size, Math.min(maxSize(updatedTextObject), originalSize))
    }

    this.updateObjectState({ size: size })

    if (metaNameSign(this.signType || "") && objs.length > 1 && this.activeObject === objs[0]) {
      if (text2Size) keepSmallerThanTitle(text2Size, objs[1])
      if (text3Size) keepSmallerThanTitle(text3Size, objs[2])
    }

    this.updateObjectState({ width: updatedTextObject.getScaledWidth() })
  }

  updateTexts(state: Partial<SignTextState>) {
    const color = state.color

    for (const obj of this.objects.all()) {
      if (isTextObj(obj)) {
        const border = obj.state.border
        const borderState = border && color ? { ...border, borderColor: color } : undefined
        this.objects.updateState(obj, { ...state, border: borderState })

        if (this.activeObject === obj && (state.left === 0 || state.top === 0)) {
          this.updateObjectPosition(obj)
        }
      }
    }
  }

  updateImages(state: Partial<SignImageState>) {
    const color = state.color

    for (const obj of this.objects.all()) {
      if (isImageObj(obj)) {
        if (!obj.state.colored) {
          const border = obj.state.border
          const borderState = border && color ? { ...border, borderColor: color } : undefined

          this.objects.updateState(obj, { ...state, border: borderState })
        }
      }
    }
  }

  /**
   * Redraws entire sign based on current state
   */
  private draw() {
    if (!this.canvas || this.disableDraw) {
      return
    }

    this.drawing = true

    const activeObj = this.canvas.getActiveObject()
    let restoreState: (() => void) | null = null
    if (activeObj && activeObj instanceof fabric.IText && activeObj.isEditing) {
      // Remember and restore inline editing state
      const selectionStart = activeObj.selectionStart || 0
      const selectionEnd = activeObj.selectionEnd || 0

      restoreState = () => {
        activeObj.enterEditing()
        activeObj.setSelectionStart(selectionStart)
        activeObj.setSelectionEnd(selectionEnd)
        this.rerender()
      }
    }

    this.canvas.renderOnAddRemove = false
    this.canvas.clear()

    for (const formObj of this.drawables.baseDrawables()) {
      if (!formObj) {
        continue
      }

      formObj.set({
        lockMovementY: true,
        lockMovementX: true,
        evented: false,
        hasControls: false,
        hasBorders: false,
        name: formObj.name ? `${formObj.name} ignore` : "ignore",
      })

      if (formObj.name?.includes("border") || formObj.clipPath) {
        formObj.clipPath = this.clipObjects
          ? this.clipShape(false, formObj.offsetX, this.drawables.clipOffsetY(formObj.offsetY))
          : undefined
      }

      this.canvas.add(formObj)
      formObj.center()
      formObj.setCoords()

      formObj.set({ top: (formObj.top || 0) + formObj.offsetY, left: (formObj.left || 0) + formObj.offsetX })
      formObj.setCoords()
    }

    for (const obj of this.objects.allByZIndex()) {
      if (obj.state.side !== (this.state.side || 0)) {
        continue
      }

      obj.clipPath = this.clipObjects ? this.clipShape(true) : undefined

      this.canvas.add(obj)
    }

    for (const formObj of this.drawables.topDrawables()) {
      if (!formObj) {
        continue
      }

      formObj.set({
        lockMovementX: true,
        lockMovementY: true,
        hasControls: false,
        hasBorders: false,
        evented: false,
        name: "top ignore",
      })

      this.canvas.add(formObj)
      formObj.center()
      formObj.setCoords()

      formObj.set({ top: (formObj.top || 0) + formObj.offsetY })
      formObj.setCoords()
    }

    this.canvas.renderAll()
    this.canvas.renderOnAddRemove = true

    if (activeObj) {
      this.canvas.setActiveObject(activeObj)
      this.canvas.renderAll()

      if (restoreState) {
        restoreState()
      }
    }

    if (!this.signCLI && !this.minimatic) {
      this.dimensions.redrawDimensions(this)
    }

    this.drawing = false
  }

  private redrawAfterZoom = debounce(() => this.draw(), 100)

  /**
   * Resizes canvas and re-centers objects based on current container size
   */
  private canvasResize = debounce((width?: number, height?: number) => {
    this.canvasResizeImmediate(width, height)
    const activeObj = this.canvas.getActiveObject()
    if (activeObj) {
      this.updateObjectPosition(activeObj as SignObject)
    }
  }, 100)

  private canvasResizeImmediate(width?: number, height?: number) {
    width = width || this.canvasWrapper.clientWidth
    height = height || this.canvasWrapper.clientHeight

    this.canvas.setWidth(width)
    this.canvas.setHeight(height)
    this.canvas.calcOffset()

    this.objects.resetPositions(this.centerX, this.centerY)
    this.draw()

    this.updateScale()

    if (this.state.shape === "contour") {
      this.queueContourUpdate()
    }
  }

  private startPan() {
    this.canvasCursor = "grab"
    this.panning = true
    this.dispatchViewportState()
  }

  private stopPan() {
    this.panning = false
    this.dispatchViewportState()
  }

  private dispatchViewportState = throttle(
    () => {
      this.onViewportStateChangedEvent.dispatch({
        zoom: this.getZoom(),
        pan: this.panning || this.panningKeyDown || this.panningToggled,
      })
    },
    16,
    { trailing: true }
  )

  private resetViewport() {
    let transform: number[] = []

    if (this.scale > 1) {
      const scaleDiff = this.scale - 1
      transform = [this.scale, 0, 0, this.scale, -(this.centerX * scaleDiff), -(this.centerY * scaleDiff)]
    } else if (this.scale < 1) {
      transform = [
        this.scale,
        0,
        0,
        this.scale,
        this.centerX - this.centerX * this.scale,
        this.centerY - this.centerY * this.scale,
      ]
    } else {
      transform = [this.scale, 0, 0, this.scale, 0, 0]
    }

    this.setViewportTransform(transform)
    this.updateContourIfNeeded()
  }

  private setViewportTransform(vpt?: number[]) {
    const current = this.canvas.viewportTransform || []
    vpt = vpt || current

    if (this.state.widthPx && this.state.heightPx) {
      const panLimit = 50 // this many pixels should always be visible
      const zoom = vpt[0] || this.canvas.getZoom()
      const width = this.canvas.getWidth()
      const height = this.canvas.getHeight()

      const centerX = -((width * zoom - width) / 2)
      const centerY = -((height * zoom - height) / 2)

      const minX = centerX - width / 2 - (this.state.widthPx / 2) * zoom
      const maxX = centerX + width / 2 + (this.state.widthPx / 2) * zoom

      const minY = centerY - height / 2 - (this.state.heightPx / 2) * zoom
      const maxY = centerY + height / 2 + (this.state.heightPx / 2) * zoom
      invariant(vpt[4])
      invariant(vpt[5])

      vpt[4] = clamp(vpt[4], minX + panLimit, maxX - panLimit)
      vpt[5] = clamp(vpt[5], minY + panLimit, maxY - panLimit)
    }

    this.canvas.setViewportTransform(vpt)
    this.dispatchViewportState()
  }

  private onViewport() {
    const vpt = this.canvas.viewportTransform || []
    const zoom = vpt[0] || this.canvas.getZoom()
    const width = this.canvas.getWidth()
    const height = this.canvas.getHeight()

    const centerX = -((width * zoom - width) / 2)
    const centerY = -((height * zoom - height) / 2)
    const maxX = width / 2 - (this.state.widthPx / 2) * zoom
    const maxY = height / 2 - (this.state.heightPx / 2) * zoom
    invariant(vpt[4])
    invariant(vpt[5])

    return Math.abs(centerX - vpt[4]) < maxX && Math.abs(centerY - vpt[5]) < maxY
  }

  private calcState(state: SignState) {
    state.widthPx = Calc.mmToPx(state.width)
    state.heightPx = Calc.mmToPx(state.height)
    state.widthIn = Calc.mmToIn(state.width)
    state.heightIn = Calc.mmToIn(state.height)

    if (state.shape === "contour" && state.contourData) {
      state.forcedWidth = state.contourData.forcedWidth
      state.forcedHeight = state.contourData.forcedHeight
    } else if (this.signType === "plastic_sign" && state.fixmethod === "wall_profile") {
      state.forcedHeight = 49
    } else {
      delete state.forcedWidth
      delete state.forcedHeight
    }
  }

  private toJSON(): SignStateV2 {
    const objects = this.objects
      .all()
      .filter(obj => {
        if (!this.state.doubleSided && (obj.state.side || 0) > 0) {
          return false
        }

        return true
      })
      .sort((a, b) => {
        if (a.type === "i-text" && b.type === "i-text") {
          const indexA = this.state.objects.indexOf((a as SignObject).state)
          const indexB = this.state.objects.indexOf((b as SignObject).state)

          if (indexA < indexB) {
            return -1
          } else if (indexA > indexB) {
            return 1
          }
        }

        return 0
      })
      .map(obj => obj.toObject())

    return {
      form: SigncodeConverter.v2form(this.state),
      objects,
      canvas: {
        width: this.canvas.getWidth(),
        height: this.canvas.getHeight(),
      },
      locale: env.locale,
    }
  }

  private priceData() {
    if (ClientData.editing) {
      if (this.state.fixedPrice === false) {
        return { fixed_price: false }
      } else if (this.state.fixedPrice === true) {
        return { fixed_price: true }
      }
    }

    return {}
  }

  private evaluateStateTesters() {
    const remove: StateTest[] = []

    for (const stateTest of this.stateTesters) {
      let result = false

      try {
        result = stateTest[0](this.state || {}, this)
      } catch (e) {
        console.error(`State test raised exception: ${e}`)
      }

      if (result) {
        remove.push(stateTest)
        document.querySelector("body")?.classList.add(stateTest[1])
      }
    }

    this.stateTesters = this.stateTesters.filter(test => !remove.includes(test))
  }

  private addGuideLines(horizontal: boolean, vertical: boolean, objGuidesX: number[], objGuidesY: number[]) {
    const stroke = "#EE00FF"
    const coords = []

    if (horizontal) {
      coords.push([
        this.centerX,
        this.centerY - this.state.heightPx / 2,
        this.centerX,
        this.centerY + this.state.heightPx / 2,
      ])
    }

    if (vertical) {
      coords.push([
        this.centerX - this.state.widthPx / 2,
        this.centerY,
        this.centerX + this.state.widthPx / 2,
        this.centerY,
      ])
    }

    for (const guide of objGuidesX) {
      coords.push([guide, this.centerY - this.state.heightPx / 2, guide, this.centerY + this.state.heightPx / 2])
    }

    for (const guide of objGuidesY) {
      coords.push([this.centerX - this.state.widthPx / 2, guide, this.centerX + this.state.widthPx / 2, guide])
    }

    for (const coordset of coords) {
      const line = new fabric.Line(coordset, {
        stroke,
        strokeWidth: 2 / this.scale,
        selectable: false,
        name: "guideline",
      })

      this.guidelines.push(line)
      this.canvas.add(line)
    }
  }

  private removeGuidelines() {
    while (this.guidelines.length > 0) {
      const line = this.guidelines.pop()

      if (line) {
        this.canvas.remove(line)
      }
    }
  }

  updateObjectPosition(obj: SignObject) {
    this.objects.objectPosition(obj)
  }

  private dispatchStateEvents(update?: Partial<SignState>) {
    if (this.clearing) {
      return
    }

    update && this.onStateUpdatedEvent.dispatch({ update, state: this.state })
    this.onSignChangedEvent.dispatch({ ...this.state })
    this.onSignDataChangedEvent.dispatch({ state: this.state, data: this.validatedData })
  }

  private triggerOnLoad() {
    this.loaded = true
    info("finishing loading")
    this.storeImmediate()
    document.querySelector("body")?.classList.add("signomaticloaded")

    if (this.minimatic) {
      document.querySelector("body")?.classList.add("minimaticloaded")
    }

    this.onLoadEvent.dispatchAsync()
    logger.finished()
  }

  private updateContourIfNeeded() {
    if (this.state.shape !== "contour") {
      return
    }

    if (this.activeObject && this.activeObject.isMoving) {
      return
    }

    if (stringifyObj({ ...this.state, contourData: null }) === this.previousContourState) {
      info("same state, skipping contour update")
      return
    }

    this.queueContourUpdate()
  }

  private externalFixmethodPadding() {
    return {
      top: Calc.pxToMm(this.drawables.externalTopPadding),
      bottom: Calc.pxToMm(this.drawables.externalBottomPadding),
      left: Calc.pxToMm(this.drawables.externalLeftPadding),
      right: Calc.pxToMm(this.drawables.externalRightPadding),
    }
  }

  private queueContourUpdate = debounce(
    () => {
      info("updating contour")

      document.querySelector("body")?.classList.add("sign-updating")

      setTimeout(() => {
        const contourData = this.contour.createPath()

        if (contourData) {
          this.previousContourState = stringifyObj({ ...this.state, contourData: null })
          this.setState({ ...this.state, contourData }, null, { contourData }, true, true)
        }

        document.querySelector("body")?.classList.remove("sign-updating")
      }, 10)
    },
    400,
    { leading: false, trailing: true }
  )
}

export default Sign
